import React from "react";
import { motion } from "framer-motion";
import { BottomSheet } from "@/components/ui/bottom-sheet";
import { Check } from "lucide-react";
import { Phone, Mail, MessageSquare, HelpCircle } from "lucide-react";
import { useFAQ } from "@/contexts/FAQContext";

interface GiftingSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

const features = [
  {
    title: "Timeless Elegance",
    items: [
      "Meticulously crafted designs",
      "Premium cotton materials",
      "Handmade details and finishes",
      "Armenian-inspired elements"
    ]
  },
  {
    title: "Personalized Experience",
    items: [
      "Expert gifting consultation",
      "Custom size recommendations",
      "Gift wrapping service",
      "Personalized message cards"
    ]
  },
  {
    title: "Special Occasions",
    items: [
      "Birthdays and celebrations",
      "Holiday collections",
      "Special events",
      "Family gatherings"
    ]
  }
];

const FeatureSection = ({ 
  title, 
  items 
}: { 
  title: string; 
  items: string[];
}) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
    viewport={{ once: true }}
    className="relative"
  >
    <h3 className="text-lg font-cinzel font-bold text-primary mb-4">{title}</h3>
    <ul className="space-y-2">
      {items.map((item, index) => (
        <li key={index} className="flex items-start gap-2 text-sm text-muted-foreground">
          <Check className="h-4 w-4 mt-1 text-primary flex-shrink-0" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  </motion.div>
);

export function GiftingSheet({ isOpen, onClose }: GiftingSheetProps) {
  const { openFAQ } = useFAQ();

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose}>
      <div className="px-4">
        {/* Header Section */}
        <motion.div 
          className="text-center mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-2xl md:text-3xl font-bold mb-3 tracking-wide font-cinzel">
            The Art of Gifting
          </h2>
          <p className="text-sm text-muted-foreground max-w-2xl mx-auto">
            Create unforgettable moments with TEL U ASEGH's luxury gifting experience.
          </p>
        </motion.div>

        {/* Introduction */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-8 text-center"
        >
          <p className="text-sm text-muted-foreground max-w-2xl mx-auto">
            Every TEL U ASEGH piece is a celebration of craftsmanship and elegance, making it the perfect choice for those special moments in life. Let us help you create a memorable gifting experience.
          </p>
        </motion.div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-8">
          {features.map((section, index) => (
            <FeatureSection
              key={index}
              title={section.title}
              items={section.items}
            />
          ))}
        </div>

        {/* Contact Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="bg-primary/5 backdrop-blur-sm rounded-2xl p-6 text-center"
        >
          <p className="text-sm text-muted-foreground mb-3">
            Let us help you choose the perfect gift. Our luxury consultants are here to assist you.
          </p>
          <p className="text-sm font-medium text-primary">
            Contact us at{" "}
            <a 
              href="mailto:cs@teluasegh.com"
              className="hover:underline"
            >
              cs@teluasegh.com
            </a>
          </p>
        </motion.div>
      </div>
    </BottomSheet>
  );
} 